import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, ArrowPathIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { getJourneySteps, bottleStateForStep, isActionDrivenStep, isRecordingStep, journeyStateForStep } from './journeySteps';
import JourneyProgress from './JourneyProgress';
import JourneyScene from './JourneyScene';
import { useLanguage } from '../../LanguageContext';

const track = (eventName: string, step: string) => {
  window.gtag?.('event', eventName, { journey_step: step, bottle_id: '7' });
};

const playStepSfx = (step: number) => {
  const audio = new Audio(`/audio/step_${step + 1}.mp3`);
  audio.volume = 0.45;
  void audio.play().catch(() => undefined);
};

const ui = {
  en: {
    presents: 'RightOZ presents',
    headline: 'Follow Your Milk',
    subheadline: 'From subscription to your doorstep — see what happens to your milk along the way',
    tagline: 'सिर्फ भरोसा नहीं, प्रमाण भी।',
    start: 'Start the Demo',
    restart: 'Restart',
    back: 'Back',
    continue: 'Continue',
    cta: 'Start Your Subscription',
    creating: 'Creating subscription…',
    success: {
      subscribe: 'Subscription created successfully',
      allotment: 'Bottle #7 assigned successfully',
      collection: 'Bottle #7 handed over successfully',
      milking: 'Recording started successfully',
      filling: 'Bottle #7 filled successfully',
      locking: 'Bottle #7 locked successfully',
      unlock: 'Bottle #7 unlocked successfully',
    },
  },
  hi: {
    presents: 'RightOZ प्रस्तुत करता है',
    headline: 'अपने दूध का सफ़र देखें',
    subheadline: 'सदस्यता से लेकर दरवाज़े तक — जानें आपके दूध के साथ क्या होता है।',
    tagline: 'सिर्फ भरोसा नहीं, प्रमाण भी।',
    start: 'डेमो शुरू करें',
    restart: 'फिर से शुरू करें',
    back: 'वापस',
    continue: 'आगे बढ़ें',
    cta: 'सदस्यता शुरू करें',
    creating: 'सदस्यता बनाई जा रही है…',
    success: {
      subscribe: 'सदस्यता सफलतापूर्वक बनाई गई।',
      allotment: 'बोतल #7 सफलतापूर्वक आवंटित की गई।',
      collection: 'बोतल #7 सफलतापूर्वक सौंप दी गई।',
      milking: 'रिकॉर्डिंग सफलतापूर्वक शुरू हुई।',
      filling: 'बोतल #7 सफलतापूर्वक भर दी गई।',
      locking: 'बोतल #7 सफलतापूर्वक बंद की गई।',
      unlock: 'बोतल #7 सफलतापूर्वक खोली गई।',
    },
  },
};

const MilkJourney: React.FC = () => {
  const { language } = useLanguage();
  const journeySteps = getJourneySteps(language);
  const t = ui[language];

  const [started, setStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [milkType, setMilkType] = useState('A2 Buffalo Milk');
  const [quantity, setQuantity] = useState(1);
  const [deliverySlots, setDeliverySlots] = useState(['morning']);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [filling, setFilling] = useState(false);
  const [filled, setFilled] = useState(false);
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [locked, setLocked] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const step = journeySteps[currentStep];
  const recording = recordingStarted && isRecordingStep(currentStep);

  // Reset to step 0 on language change so index always aligns
  useEffect(() => {
    setCurrentStep(0);
    setLocked(false);
    setUnlocked(false);
    setDeliverySlots(['morning']);
    setRecordingSeconds(0);
    setRecordingStarted(false);
    setSubscribing(false);
    setAssigning(false);
    setHandingOver(false);
    setFilling(false);
    setFilled(false);
    setLocking(false);
    setUnlocking(false);
  }, [language]);

  useEffect(() => {
    if (!started) return;
    track('journey_step_viewed', step.id);
    if (step.id === 'proof') track('journey_completed', step.id);
  }, [currentStep, started, step.id]);

  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  const goTo = (nextStep: number) => {
    const safeStep = Math.max(0, Math.min(nextStep, journeySteps.length - 1));
    setSubscribing(false);
    setAssigning(false);
    setHandingOver(false);
    setFilling(false);
    setLocking(false);
    setUnlocking(false);
    const state = journeyStateForStep(safeStep);
    setRecordingStarted(state.recordingStarted);
    setFilled(state.filled);
    setLocked(state.locked);
    setUnlocked(state.unlocked);
    if (safeStep <= 4) setRecordingSeconds(0);
    setCurrentStep(safeStep);
  };
  const next = () => {
    if (step.id !== 'unlock') playStepSfx(currentStep);
    if (step.id === 'locking') setLocked(true);
    if (step.id === 'unlock') setUnlocked(true);
    goTo(currentStep + 1);
  };
  useEffect(() => {
    if (!subscribing && !assigning && !handingOver && !filling && !locking) return;
    const timer = window.setTimeout(() => {
      setCurrentStep((current) => Math.min(current + 1, journeySteps.length - 1));
      setSubscribing(false);
      setAssigning(false);
      setHandingOver(false);
      setFilling(false);
      setLocking(false);
    }, assigning ? 2200 : 1900);
    return () => window.clearTimeout(timer);
  }, [subscribing, assigning, handingOver, filling, locking, journeySteps.length]);
  useEffect(() => {
    if (!unlocking) return;
    const timer = window.setTimeout(() => {
      setUnlocked(true);
      setUnlocking(false);
    }, 3600);
    return () => window.clearTimeout(timer);
  }, [unlocking]);
  const restart = () => {
    setCurrentStep(0);
    setMilkType('A2 Buffalo Milk');
    setQuantity(1);
    setDeliverySlots(['morning']);
    setRecordingSeconds(0);
    setRecordingStarted(false);
    setSubscribing(false);
    setAssigning(false);
    setHandingOver(false);
    setFilling(false);
    setFilled(false);
    setLocking(false);
    setUnlocking(false);
    setLocked(false);
    setUnlocked(false);
    track('journey_restarted', 'subscribe');
  };
  const action = () => {
    const events: Record<string, string> = {
      subscribe: 'journey_subscription_selected',
      allotment: 'journey_bottle_assigned',
      collection: 'journey_collection_completed',
      milking: 'journey_recording_started',
      locking: 'journey_bottle_locked',
      unlock: 'journey_unlock_completed',
    };
    if (step.id === 'subscribe') {
      playStepSfx(currentStep);
      setSubscribing(true);
      track(events.subscribe, step.id);
      return;
    }
    if (step.id === 'allotment') {
      playStepSfx(currentStep);
      setAssigning(true);
      track(events.allotment, step.id);
      return;
    }
    if (step.id === 'collection') {
      playStepSfx(currentStep);
      setHandingOver(true);
      track(events.collection, step.id);
      return;
    }
    if (step.id === 'milking') {
      if (!recordingStarted) {
        playStepSfx(currentStep);
        setRecordingStarted(true);
        track(events.milking, step.id);
      } else {
        goTo(currentStep + 1);
      }
      return;
    }
    if (step.id === 'filling') {
      playStepSfx(currentStep);
      setFilled(true);
      setFilling(true);
    }
    if (step.id === 'locking') {
      playStepSfx(currentStep);
      setLocked(true);
      setLocking(true);
    }
    if (step.id === 'unlock') {
      setUnlocking(true);
      window.setTimeout(() => playStepSfx(currentStep), 1000);
    }
    track(events[step.id] || 'journey_action_completed', step.id);
  };
  const actionLabel = step.id === 'subscribe' && subscribing
    ? t.creating
    : step.id === 'allotment' && assigning
      ? language === 'hi' ? 'बोतल आवंटित की जा रही है…' : 'Assigning bottle…'
    : step.id === 'collection' && handingOver
    ? language === 'hi' ? 'बोतल सौंपी जा रही है…' : 'Handing over bottle…'
    : step.id === 'milking' && recordingStarted
    ? journeySteps[5].action
    : step.id === 'filling' && filling
      ? language === 'hi' ? 'बोतल भरी जा रही है…' : 'Filling bottle…'
      : step.id === 'locking' && locking
        ? language === 'hi' ? 'बोतल बंद की जा रही है…' : 'Locking bottle…'
        : step.id === 'unlock' && unlocking
          ? language === 'hi' ? 'बोतल खोली जा रही है…' : 'Opening bottle…'
        : step.action;
  const successMessage = step.id === 'subscribe' && subscribing
    ? t.success.subscribe
    : step.id === 'allotment' && assigning
      ? t.success.allotment
    : step.id === 'collection' && handingOver
      ? t.success.collection
      : step.id === 'milking' && recordingStarted
        ? t.success.milking
        : step.id === 'filling' && filling
          ? t.success.filling
          : step.id === 'locking' && locking
            ? t.success.locking
            : step.id === 'unlock' && unlocked
              ? t.success.unlock
              : undefined;
  const subscribe = () => {
    playStepSfx(currentStep);
    track('journey_cta_clicked', 'signup');
    window.dispatchEvent(new Event('rightoz:open-subscription'));
  };

  return (
    <section id="journey" className="scroll-mt-20 bg-[var(--color-cream)] py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {!started ? (
          <div className="overflow-hidden rounded-[2.25rem] bg-[linear-gradient(135deg,#103b16,#1b5e20)] px-6 py-14 text-center text-white shadow-[0_24px_60px_rgba(27,94,32,0.22)] sm:px-12">
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#c4dab7]">{t.presents}</p>
            <h2 className="mt-4 font-display text-5xl font-bold sm:text-7xl">{t.headline}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[#e1ead8]">{t.subheadline}</p>
            <p className="mt-4 text-xl font-semibold">{t.tagline}</p>
            <button type="button" onClick={() => { setStarted(true); track('journey_started', 'intro'); }} className="mt-8 rounded-full bg-white px-7 py-4 font-semibold text-[var(--color-forest)] transition hover:bg-[#f0f5ec] focus:outline-none focus:ring-4 focus:ring-[rgba(255,255,255,0.35)]">
              {t.start} <ArrowRightIcon className="ml-2 inline h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-[2.25rem] border border-[rgba(27,94,32,0.12)] bg-white shadow-[0_20px_60px_rgba(27,94,32,0.1)]">
            <JourneyProgress currentStep={currentStep} steps={journeySteps} language={language} onSelect={goTo} />
            <JourneyScene
              step={step}
              index={currentStep}
              bottleState={step.id === 'filling' && !filled ? 'empty' : bottleStateForStep(currentStep, unlocked, locked)}
              unlocked={unlocked}
              assigning={assigning}
              recording={recording}
              handingOver={handingOver}
              filling={filling}
              locking={locking}
              unlocking={unlocking}
              recordingSeconds={recordingSeconds}
              milkType={milkType}
              quantity={quantity}
              deliverySlots={deliverySlots}
              onMilkTypeChange={setMilkType}
              onQuantityChange={setQuantity}
              onDeliverySlotsChange={setDeliverySlots}
              onAction={action}
              actionLabel={actionLabel}
              actionDisabled={subscribing || assigning || handingOver || filling || locking || unlocking || unlocked}
              successMessage={successMessage}
              language={language}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgba(27,94,32,0.12)] px-5 py-5 sm:px-8">
              <button type="button" onClick={restart} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-forest)]"><ArrowPathIcon className="h-4 w-4" />{t.restart}</button>
              <div className="flex gap-3">
                <button type="button" onClick={() => goTo(currentStep - 1)} disabled={currentStep === 0} className="inline-flex items-center gap-2 rounded-full border border-[rgba(27,94,32,0.2)] px-4 py-2 text-sm font-semibold text-[var(--color-forest)] disabled:cursor-not-allowed disabled:opacity-40"><ArrowLeftIcon className="h-4 w-4" />{t.back}</button>
                {currentStep === journeySteps.length - 1 ? <button type="button" onClick={subscribe} className="rounded-full bg-[var(--color-forest)] px-5 py-2 text-sm font-semibold text-white">{t.cta}</button> : (!isActionDrivenStep(step.id) || (step.id === 'unlock' && unlocked && !unlocking)) && <button type="button" onClick={next} className="inline-flex items-center gap-2 rounded-full bg-[var(--color-forest)] px-5 py-2 text-sm font-semibold text-white">{t.continue} <ArrowRightIcon className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MilkJourney;
